<!--

===========================================================================================================================
||    DurationWrapper component         - another version of TimeFrequency component. Join these in one durring rewrite  ||
===========================================================================================================================

|   Description
------------------------------------------------------------------------------------------------------------
  Component enables to input time frequency in minutes, hours or days providing manual number input and dropdown for time scope.
  As output it gives validation, time in seconds, time input and time scope values.

|   Features
------------------------------------------------------------------------------------------------------------
  - Validation - Both fields are required and time input has to be in range of 5 minutes to 30 days by default or as set via props.

|   Props - props are passed via schema componentsConfig which should be changed durring rewrite and standardised
------------------------------------------------------------------------------------------------------------
  - value             : Object    : Used for two-way binding. Contains valid, timeInput, timeScope and seconds.
  - minSeconds        : Number    : Minimum seconds value. Default 300.
  - minSecondsMessage : String    : Error message for minimum seconds value. Default 'Retrigger period can\'t be less than 5 minutes.'
  - maxSeconds        : Number    : Maximum seconds value. Default 30 days.
  - maxSecondsMessage : String    : Error message for maximum seconds value. Default 'Retrigger period can\'t exceed 30 days.'

|   Events
------------------------------------------------------------------------------------------------------------
  - input       : Object    : Emits object with valid, timeInput, timeScope and seconds values.

|   v-model
------------------------------------------------------------------------------------------------------------
  {
    valid: [Boolean],
    data: [Number],
  }

|   TODO
------------------------------------------------------------------------------------------------------------
  - [min/max]SecondsMessage - Find highest possible scope like in setInitialValues method and set the default messages. If user overrides it then ignore the default messages.

-->

<template>
  <ValidationObserver
    ref="timeFrequencyObserver"
    :key="keyReset"
    v-slot="{ errors }" >
    <div class="time-frequency flex center-all gap-8">
      <ValidationProvider
        ref="timeInputProvider"
        name="Time"
        class="w-100"
        :rules="dynamicValidators" >
        <input
          ref="timeInputRef"
          v-model="timeInput"
          type="number"
          min="0"
          class="time-input sb2-bordered sb2-hoverable sb2-focusable mr-2 w-100"
          :class="{ empty: !timeInput }"
          :placeholder="`Enter ${timeScope.name ? timeScope.name.toLowerCase() : ''}`" >
      </ValidationProvider>
      <multiselect
        ref="scopeDropdown"
        v-model="timeScope"
        v-select-overflow
        placeholder="Select"
        class="flex-options hide-tags no-selected-background shrink-2"
        :options="visibleTimeScopes"
        label="name"
        track-by="name"
        :multiple="false"
        :show-labels="false"
        :show-no-results="false"
        :close-on-select="true"
        :clear-on-select="false"
        :preserve-search="true"
        :allow-empty="false"
        :searchable="false"
        preselect-first
        @open="opened" >
        <template
          slot="option"
          slot-scope="props">
          <span class="ellipsis greedy">
            {{ props.option.name }}
          </span>
          <SBIcon
            :icon="SBIconList.DONE_CIRCLE"
            :color="SBColors.bluePrimary"
            :size="13"/>
        </template>
      </multiselect>
    </div>
    <div
      v-if="error || (errors.Time && errors.Time.length)"
      class="error flex flex-align-center">
      <SBIcon
        class="mr-2"
        :icon="SBIconList.ERROR"
        :color="SBColors.redPrimary"
        :size="12" />
      <span>{{ error || errors.Time[0] }}</span>
    </div>
  </ValidationObserver>
</template>

<script>
import { mapGetters } from 'vuex';

import Multiselect from 'vue-multiselect';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';

export default {
  name: 'DurationWrapper',
  components: {
    Multiselect,
    SBIcon,
  },
  props: {
    schema: {
      type: Object,
      default: () => ({}),
    },
    value: {
      type: Object,
      default: () => {},
    },
    minSeconds: {
      type: Number,
      default: 300,
    },
    minSecondsMessage: {
      type: String,
      default: 'Retrigger period can\'t be less than 5 minutes.',
    },
    maxSeconds: {
      type: Number,
      default: 30 * 24 * 60 * 60, // 30 days
    },
    maxSecondsMessage: {
      type: String,
      default: 'Retrigger period exceeds maximum allowed time.',
    },
    validators: {
      type: String,
      default: '',
    },
    propName: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      timeInput: '',
      timeScope: {},
      timeScopeTypes: [
        {
          name: 'Minutes',
          factor: 60,
        },
        {
          name: 'Hours',
          factor: 3600,
        },
        {
          name: 'Days',
          factor: 86400,
        },
      ],
      preventInitialChange: false,
      scopeDropdownOpened: false,
      error: null,
      toggled: false,
      preventEmit: false,
      toggleBackup: {
        timeInput: '',
        timeScope: '',
      },
      keyReset: 0,
      SBIconList,
      SBColors,
    };
  },
  computed: {
    ...mapGetters({
      stepperDataStore: 'helpers/stepperDataStore',
    }),
    visibleTimeScopes() {
      return this.timeScopeTypes.filter(scope => !this.schema.hideScopes.map(s => s.toLowerCase()).includes(scope.name.toLowerCase()));
    },
    dynamicValidators() {
      const regex = new RegExp('requiredIf:([^,|\\n]*)?', '');
      const targetKey = this.validators.match(regex);
      if (targetKey && targetKey[1]) {
        const targetValue = this.getObjectCopyByKey(this.stepperDataStore, targetKey[1]);
        const targetHasValue =
          (targetValue && targetValue.value) ||
          (targetValue && targetValue.data && targetValue.data.buttons && targetValue.data.buttons.length) ||
          (targetValue && targetValue.data && targetValue.data.actions && targetValue.data.actions.data && targetValue.data.actions.data.value && targetValue.data.actions.data.value.length);
        if (targetHasValue) {
          return this.validators.replace(/requiredIf:([^|\n]*)?/g, 'required');
        }
      }

      let validators = this.validators.replace(/requiredIf:([^|\n]*)?\|*/g, '');
      validators = !this.toggled ?
        this.validators :
        this.validators.split('|').filter(v => v === 'required').length ?
          this.validators :
          `${this.validators}|required`;
      return validators;
    },
    /** This message dynamically changes the max seconds error message to show the value in currently selected scope. */
    // maxSecondsMessageComputed() {
    //   return this.maxSecondsMessage || `Retrigger period can't exceed ${this.maxSeconds / this.timeScope.factor} ${this.timeScope.name.toLowerCase()}.`;
    // },
  },
  watch: {
    dynamicValidators() {
      this.$nextTick(() => {
        this.emit();
      });
    },
    timeInput() {
      this.emit();
    },
    timeScope() {
      this.emit();
    },
    value: {
      handler() {
        if (this.preventInitialChange) {
          return;
        }
        this.preventInitialChange = true;
        this.setInitialValues();
      },
      deep: true,
    },
  },
  mounted() {
    this.setInitialValues();
    if (this.schema.stepID) {
      this.$eventBus.$on(`validate-${this.schema.stepID}`, () => {
        if (!this.$refs.timeFrequencyObserver) return;
        this.$refs.timeFrequencyObserver.validate();
      });
    }
    if (this.schema.toggle) {
      this.toggled = this.schema.toggle;
    }
  },
  methods: {
    focus() {
      this.$refs.timeInputRef.focus();
    },
    /** If multiselect is clicked and the error is shown on some field above in that moment then the multiselect dropdown will not be positioned correctly so we need to hide it initially and show it again after th screen is updated. */
    opened() {
      // console.log('opened');
      if (this.scopeDropdownOpened) {
        this.scopeDropdownOpened = false;
      } else {
        this.$refs.scopeDropdown.deactivate();
        this.scopeDropdownOpened = true;
        setTimeout(() => this.openScopeDropdown(), 0);
      }
    },
    openScopeDropdown() {
      this.$refs.scopeDropdown.activate();
    },
    /**
     * Sets initial values for time input and time scope from value.
     * It finds highest possible scope for given seconds value.
     */
    setInitialValues() {
      // console.log('SET INITIAL VALUES', this.schema.value);
      this.timeInput = this.schema.noDefault ? null : Number.isInteger(Number.parseInt(this.schema.value, 10)) ? this.schema.value / this.visibleTimeScopes[0].factor : this.minSeconds ? this.minSeconds / this.visibleTimeScopes[0].factor : null;
      this.timeScope = this.visibleTimeScopes[0];

      this.visibleTimeScopes.slice().reverse().some((scope) => {
        const seconds = Number.isInteger(Number.parseInt(this.schema.value, 10)) ? Number.parseInt(this.schema.value, 10) : this.minSeconds;
        if (seconds % scope.factor === 0 && !(scope.factor === seconds && seconds === 86400)) {
          this.timeInput = this.schema.noDefault ? null : Number.isInteger(seconds) ? seconds / scope.factor : null;
          this.timeScope = scope;
          return true;
        }
        return false;
      });

      this.$nextTick(() => this.emit(true));
    },
    /**
     * Validates if time input is in range of minSeconds and maxSeconds.
     * @returns {Boolean} - True if time input is in range, false otherwise.
     */
    validSecondsRange() {
      // console.log('VALID SECONDS RANGE', this.timeInput, this.timeScope.factor, this.maxSeconds);
      if (this.validators.indexOf('required') === -1 && !this.timeInput) {
        this.error = null;
      } else if (this.timeInput * this.timeScope.factor > this.maxSeconds) {
        this.error = this.maxSecondsMessage;
      } else if (this.timeInput * this.timeScope.factor < this.minSeconds) {
        this.error = this.minSecondsMessage;
      } else {
        this.error = null;
      }
      return this.error === null;
    },
    /**
     * Emits input event.
     */
    emit(reset = false) {
      if (this.preventEmit) return;
      if (!this.$refs.timeFrequencyObserver) return;
      this.$refs.timeFrequencyObserver.validate()
        .then((valid) => {
          this.$emit('input', {
            valid: !this.toggled || (valid && this.validSecondsRange()),
            data: {
              value: this.timeInput * this.timeScope.factor,
              toggle: this.toggled,
            },
          });
        });
      if (reset) this.$refs.timeFrequencyObserver.reset();
    },
    setToggle(state) {
      this.toggled = state;
      if (!state) {
        this.preventEmit = true;
        setTimeout(() => {
          this.toggleBackup.timeInput = this.timeInput;
          this.toggleBackup.timeScope = { ...this.timeScope };

          this.timeInput = 0;
          this.keyReset++;

          this.$nextTick(() => {
            this.preventEmit = false;
            this.emit();

            if (this.$refs.timeInputProvider) {
              this.$refs.timeInputProvider.reset();
            }
          });
        }, 500);
      } else {
        this.preventEmit = true;
        if (this.toggleBackup.timeInput) this.timeInput = this.toggleBackup.timeInput;
        this.$nextTick(() => {
          this.preventEmit = false;
          this.emit();
          if (this.$refs.timeInputProvider) {
            setTimeout(() => {
              this.$refs.timeInputProvider.reset();
            }, 0);
          }
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.time-frequency {
  .time-input {
    font-size: 14px;
    padding-top: 6px;
    height: 34px;
  }
}

.error {
  font-size: 14px;
  color: $redPrimary;
  margin-bottom: 3px;
}

::v-deep .flex-options {
  .multiselect__select, .multiselect__tags {
    height: 34px !important;
  }
}
</style>
